import React, { useState } from "react";
import { useSelector } from "react-redux";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { LanguageLoader } from "../../assets/languages";
import { GetSSRSReportAPI, CheckHasManualParameterAPI } from "../../services";
import { ConvertNumbers, DownloadByContent, Toastify } from "../utils";
import FieldContainer from "./FieldContainer";
import Modal from "./Modal";

export const useRahkaranReportRunner = (parentID = '', onComplete = null) =>
{
	const preferences = useSelector((state) => state.preferences);
	const language = LanguageLoader(preferences.language);
	const [showParamModal, SetShowParamModal] = useState(false);
	const [hideParamModal, SetHideParamModal] = useState(false);
	const [reportParams, SetReportParams] = useState([]);
	const [paramValues, SetParamValues] = useState({});
	const [currentReportID, SetCurrentReportID] = useState(null);

	const RunReport = (reportID, parameters = {}) =>
	{
		GetSSRSReportAPI(reportID, parentID, parameters).then(response =>
		{
			if(response && response.content)
			{
				DownloadByContent(response.content, response.name);
			}
			if(onComplete)
			{
				onComplete();
			}
		});
	}

	const FetchRahkaranReport = async (reportID) =>
	{
		const response = await CheckHasManualParameterAPI(reportID, parentID);
		const params = response?.parameteres || response?.parameters || [];
		const manualParams = params.filter(param => param.fill_method === 'manual');

		if(manualParams.length)
		{
			SetCurrentReportID(reportID);
			SetReportParams(manualParams);
			SetParamValues({});
			SetHideParamModal(false);
			SetShowParamModal(true);
		}
		else
		{
			RunReport(reportID);
		}
	}

	const HandleParamChange = (name, val) =>
	{
		SetParamValues(prev => ({...prev, [name]: val}));
	}

	const HandleParamSubmit = () =>
	{
		for(const param of reportParams)
		{
			if(param.mandatory === '1' && !paramValues[param.name])
			{
				Toastify(param.label + ' ' + language.ERROR.IS_REQUIRED);
				return false;
			}
		}
		RunReport(currentReportID, paramValues);
		SetHideParamModal(true);
		return true;
	}

	const HandleParamClose = () =>
	{
		SetShowParamModal(false);
		SetReportParams([]);
		SetParamValues({});
		SetCurrentReportID(null);
	}

	const RenderParamField = (param) =>
	{
		const isRequired = param.mandatory === '1';
		const fieldValue = paramValues[param.name] || '';

		if(param.type === 'date')
		{
			return (
				<FieldContainer key={param.name} label={param.label} required={isRequired} className="w-full">
					<DatePicker
						onChange={(e) => HandleParamChange(param.name, e ? ConvertNumbers(e.format()) : '')}
						arrow={false}
						hideOnScroll
						portal
						zIndex={9999}
						value={fieldValue}
						inputClass="date-picker"
						containerClassName="w-full"
						calendar={preferences.language === 'en_US' ? null : persian}
						locale={preferences.language === 'en_US' ? null : persian_fa}
						calendarPosition="bottom-center"
						format="YYYY/MM/DD"
					/>
				</FieldContainer>
			);
		}

		if(param.type === 'datetime')
		{
			return (
				<FieldContainer key={param.name} label={param.label} required={isRequired} className="w-full">
					<DatePicker
						onChange={(e) => HandleParamChange(param.name, e ? ConvertNumbers(e.format()) : '')}
						arrow={false}
						hideOnScroll
						portal
						zIndex={9999}
						value={fieldValue}
						inputClass="date-picker"
						containerClassName="w-full"
						calendar={preferences.language === 'en_US' ? null : persian}
						locale={preferences.language === 'en_US' ? null : persian_fa}
						calendarPosition="bottom-center"
						format="YYYY/MM/DD HH:mm"
						plugins={[<TimePicker position="bottom" key="time"/>]}
					/>
				</FieldContainer>
			);
		}

		return (
			<FieldContainer key={param.name} label={param.label} required={isRequired} className="w-full">
				<input
					type="text"
					className="form-control"
					value={fieldValue}
					onChange={(e) => HandleParamChange(param.name, e.target.value)}
				/>
			</FieldContainer>
		);
	}

	// Element, not a component function — prevents remount on every state change
	const paramModal = (
		<Modal
			handler={<></>}
			trigger={showParamModal}
			fallDown={hideParamModal}
			onSubmit={HandleParamSubmit}
			onClose={HandleParamClose}
			title={language.RAHKARAN_REPORTS}
			fitContent
			size={{width: '28rem'}}
			autoFocus={false}
		>
			<div className="flex flex-col gap-2">
				{reportParams.map(param => RenderParamField(param))}
			</div>
		</Modal>
	);

	return { FetchRahkaranReport, paramModal };
}
